// Rate resolution types extracted to break circular dependency between
// member-resource.ts and time-entry.ts

export const ResolvedFromValues = [
  'TASK_RATE',
  'PROJECT_PERSON',
  'PROJECT_RATE_CARD',
  'CLIENT_PERSON',
  'CLIENT_RATE_CARD',
  'PERSON_DEFAULT',
  'ROLE_DEFAULT',
  'ORG_DEFAULT',
] as const

export type ResolvedFrom = (typeof ResolvedFromValues)[number]

// RateSnapshot — full cascade context captured at rate resolution time.
// Stored as JSONB on time_entry.rate_snapshot. Financial reports read the
// captured currencies rather than guessing from current rate settings.
export interface RateSnapshot {
  snapshotVersion: 1

  /** Immutable source terms for a deliverable-specific blended bill rate. */
  billingBasis?: {
    kind: 'DELIVERABLE_BLENDED'
    deliverableId: string
    projectId: string
    billRate: number
    currency: string
  }

  // The winning result after cascade + modifiers
  resolved: {
    billRate: number
    costRate: number
    payRate: number
    resolvedFrom: ResolvedFrom
    currency: string | null
    /** Present for blended project rates; null means the bill currency was unknown. */
    billCurrency?: string | null
    /** Source currencies of independently resolved labor rates. Absent on legacy snapshots. */
    payCurrency?: string | null
    costCurrency?: string | null
  }

  // Post-cascade modifiers applied to produce the final rates
  modifiers: {
    rateType: 'FIXED' | 'COST_PLUS' | 'AT_COST'
    pricingModel: 'STANDARD' | 'BLENDED' | null
    discountPercent: number | null
    discountSource: 'PROJECT' | 'CLIENT' | null
    originalBillRate: number | null
  }

  // Member context at time of resolution
  member: {
    employmentType: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR' | 'FREELANCER'
    employmentCategory: 'W2' | 'CONTRACTOR'
    roleId: string | null
    roleName: string | null
    rateSource: 'MANUAL' | 'COMPENSATION'
    compensationId: string | null
  }

  // Organization default rate (level 8)
  orgDefault: {
    billRate: number
    costRate: number
  }

  // Rate card that matched (levels 3, 5, 7)
  rateCard: {
    id: string
    name: string
    effectiveFrom: string
    effectiveTo: string | null
    assignedVia: 'PROJECT' | 'ENTITY' | null
    entry: {
      id: string
      roleId: string
      roleName: string
      billRate: number
    }
  } | null

  // All member rates evaluated during cascade (ordered by priority)
  memberRates: Array<{
    level: ResolvedFrom
    id: string
    billRate: number
    costRate: number
    payRate: number
    rateType: 'FIXED' | 'COST_PLUS' | 'AT_COST'
    rateSource: 'MANUAL' | 'COMPENSATION'
    projectId: string | null
    entityId: string | null
    taskId: string | null
    effectiveFrom: string
    effectiveTo: string | null
  }>

  // Person default rate (level 6)
  personDefault: {
    id: string
    billRate: number
    costRate: number
    payRate: number
    rateSource: 'MANUAL' | 'COMPENSATION'
  } | null
}
